<template>
  <button
    class="theme-toggle"
    :class="{ 'is-dark': isDark }"
    :title="isDark ? $t('user.theme.light') : $t('user.theme.dark')"
    @click="toggle"
  >
    <span class="theme-toggle-track">
      <span class="theme-toggle-thumb">
        <SunIcon v-if="!isDark" :size="12" />
        <MoonIcon v-else :size="12" />
      </span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { SunIcon, MoonIcon } from 'lucide-vue-next'
import { useTheme } from '@/composables/useTheme'

const { theme, setTheme } = useTheme()

const isDark = computed(() => {
  if (theme.value === 'dark') return true
  if (theme.value === 'light') return false
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
})

function toggle() {
  setTheme(isDark.value ? 'light' : 'dark')
}
</script>

<style scoped>
.theme-toggle {
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  margin: 0;
  outline: none;
}

.theme-toggle:focus-visible .theme-toggle-track {
  outline: 2px solid rgba(255, 255, 255, 0.6);
  outline-offset: 2px;
}

.theme-toggle-track {
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  transition: background 0.2s, border-color 0.2s;
}

.theme-toggle:hover .theme-toggle-track {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.28);
}

.theme-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: grid;
  place-items: center;
  color: var(--color-primary, #1B4FB2);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              background 0.25s,
              color 0.25s;
}

.is-dark .theme-toggle-thumb {
  transform: translateX(20px);
  background: var(--color-surface-high, #22262E);
  color: var(--color-secondary, rgb(96, 165, 250));
}

.theme-toggle-thumb svg {
  width: 12px;
  height: 12px;
}
</style>
